import { HeadingNumeral } from "@/components/marketplace/layout/heading-numeral"
import { ArrowIcon, Heading, LinkButton, Text } from "@/components/ui"
import { type ImageCrop, TileImage } from "@/components/ui/tile-image"

interface PaymentPlanInstallment {
  when: string
  amount: string
}

interface PaymentPlanProps {
  receiptCrop?: ImageCrop
  planCrop?: ImageCrop
  productCrop?: ImageCrop
  planImageAlt?: string
  productImageAlt?: string
  receiptImage: string
  receiptImageAlt: string
  receiptTitle: string
  receiptBody: string
  receiptSello?: string
  planImage?: string
  planTitle: string
  planBody: string
  cardHeading: string
  cardBrand: string
  productName: string
  productMeta: string
  productImage?: string
  todayLabel: string
  todayAmount: string
  installments: PaymentPlanInstallment[]
  totalLabel: string
  totalAmount: string
  cta?: string
  ctaHref?: string
}

export const PaymentPlan = ({
  receiptCrop,
  planCrop,
  productCrop,
  planImageAlt,
  productImageAlt,
  receiptImage,
  receiptImageAlt,
  receiptTitle,
  receiptBody,
  receiptSello,
  planImage,
  planTitle,
  planBody,
  cardHeading,
  cardBrand,
  productName,
  productMeta,
  productImage,
  todayLabel,
  todayAmount,
  installments,
  totalLabel,
  totalAmount,
  cta,
  ctaHref,
}: PaymentPlanProps) => (
  <div className="@container flex flex-col gap-600">
    <div className="rounded-20x bg-surface-receipt overflow-hidden">
      <div className="aspect-strip @2xl:aspect-strip-wide relative">
        <TileImage src={receiptImage} crop={receiptCrop} alt={receiptImageAlt} fill sizes="(min-width: 1024px) 96vw, 100vw" className="object-cover" />
      </div>
      <div className="@2xl:grid-cols-receipt grid items-end gap-600 p-600 @2xl:gap-1200 @2xl:p-800">
        <Heading as="h3" variant="displayLg" family="display" tone="receipt" balance className="@2xl:text-display-2xl max-w-title">
          <HeadingNumeral>{receiptTitle}</HeadingNumeral>
        </Heading>
        <div className="flex flex-col items-start gap-400">
          <Text as="p" variant="bodyLg" family="text" tone="inherit" className="text-fg-receipt-muted max-w-measure">
            {receiptBody}
          </Text>
          {receiptSello && (
            <Text as="span" variant="eyebrow" tone="receipt" className="flex items-center gap-200">
              <span aria-hidden className="bg-brand size-2 shrink-0 rounded-full" />
              {receiptSello}
            </Text>
          )}
        </div>
      </div>
    </div>

    <div className="rounded-20x bg-nelo-black-soft @2xl:grid-cols-plan relative isolate grid items-center gap-700 overflow-hidden bg-cover bg-center p-700 @2xl:gap-1200 @2xl:p-1200">
      {planImage && <TileImage src={planImage} crop={planCrop} alt={planImageAlt ?? ""} data-image-purpose="background" fill sizes="1800px" className="-z-10 object-cover" />}
      <div className="flex flex-col gap-400">
        <Heading as="h3" variant="displayLg" family="display" tone="inverted" balance className="@2xl:text-display-2xl max-w-title">
          {planTitle}
        </Heading>
        <Text as="p" variant="bodyLg" family="text" tone="invertedMuted" className="max-w-measure-md">
          {planBody}
        </Text>
      </div>

      <div className="bg-surface rounded-20x shadow-modal w-full max-w-80 justify-self-center p-250">
        <div className="rounded-16x bg-surface overflow-hidden">
          <Text as="div" variant="bodySm" family="display" tone="strong" className="flex items-center justify-between px-400 pt-400 pb-250 font-semibold">
            <span>{cardHeading}</span>
            <span className="text-fg-muted tracking-widest">{cardBrand}</span>
          </Text>
          <div className="flex items-center gap-300 px-400 pt-150 pb-400">
            <span className="bg-surface-subtle rounded-8x relative size-14 shrink-0 overflow-hidden">
              {productImage && <TileImage src={productImage} crop={productCrop} alt={productImageAlt ?? ""} fill sizes="56px" className="object-contain p-150" />}
            </span>
            <div className="min-w-0">
              <Text as="strong" variant="body" family="text" tone="strong" className="block font-semibold">
                {productName}
              </Text>
              <Text as="span" variant="bodySm" family="text" tone="muted" numeric className="mt-50 block">
                {productMeta}
              </Text>
            </div>
          </div>
          <div className="rounded-12x bg-brand-subtle mx-400 flex items-baseline justify-between px-400 py-350">
            <Text as="span" variant="body" family="text" tone="brand" className="font-semibold">
              {todayLabel}
            </Text>
            <Heading as="b" variant="lg" family="display" tone="brand" numeric>
              {todayAmount}
            </Heading>
          </div>
          <ol className="mt-350 px-400">
            {installments.map((installment) => (
              <li key={installment.when} className="border-line-subtle flex items-center gap-300 border-t py-300 first:border-t-0">
                <span aria-hidden className="bg-brand size-2 shrink-0 rounded-full" />
                <Text as="span" variant="body" family="text" tone="default" numeric className="flex-1">
                  {installment.when}
                </Text>
                <Text as="b" variant="body" family="text" tone="strong" numeric className="font-semibold">
                  {installment.amount}
                </Text>
              </li>
            ))}
          </ol>
          <div className="border-line-subtle mt-350 flex items-baseline justify-between border-t px-400 pt-350 pb-500">
            <Text as="span" variant="bodySm" family="text" tone="muted">
              {totalLabel}
            </Text>
            <Heading as="b" variant="sm" family="display" tone="strong" numeric>
              {totalAmount}
            </Heading>
          </div>
        </div>
      </div>
    </div>

    {cta && (
      <LinkButton href={ctaHref} variant="primary" size="lg" className="lift self-start font-extrabold" iconRight={<ArrowIcon size={18} />}>
        {cta}
      </LinkButton>
    )}
  </div>
)
